import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import { IGenerateAnswersProps, IQasAnswerMakerState, IResponseGenerateAnswerStatus } from "@/types/qas/qasAnswerMakerTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasAnswerMakerState = {
	answerMakerStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	answerMakerStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	answerMakerStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
	},
};

// генерация кешированных ответов
export const generateAnswers = createAsyncThunk(
	"qasAnswerMaker/start",
	async ({ categoryId, channelId, mode, option, variants, replace, modelGpt, promptTemplateId }: IGenerateAnswersProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.ANSWER_MAKER_START}`, {
				params: {
					category: categoryId,
					channel: channelId,
					mode,
					option,
					variants,
					replace,
					model: modelGpt,
					template: promptTemplateId,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// остановка генерации кешированных ответов
export const stopGenerateAnswers = createAsyncThunk("qasAnswerMaker/stop", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.ANSWER_MAKER_STOP}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус генерации кешированных ответов
export const getStatusGenerateAnswers = createAsyncThunk(
	"qasAnswerMaker/status",
	async (_, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseGenerateAnswerStatus> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.ANSWER_MAKER_STATUS}`);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasAnswerMaker.answerMakerStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

const qasAnswerMakerSlice = createSlice({
	name: "qasAnswerMaker",
	initialState,
	reducers: {
		// очистка state
		clearState: (state) => {
			state.answerMakerStart = initialState.answerMakerStart;
			state.answerMakerStop = initialState.answerMakerStop;
			state.answerMakerStatus = initialState.answerMakerStatus;
		},
		// очистка статуса старта генерации ответов
		clearAnswerMakerStart: (state) => {
			state.answerMakerStart = initialState.answerMakerStart;
		},
		// очистка статуса остановки генерации ответов
		clearAnswerMakerStop: (state) => {
			state.answerMakerStop = initialState.answerMakerStop;
		},
		// очистка статуса процесса генерации ответов
		clearAnswerMakerStatus: (state) => {
			state.answerMakerStatus = initialState.answerMakerStatus;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(generateAnswers.pending, (state) => {
				state.answerMakerStart.status = RequestStatus.LOADING;
			})
			.addCase(generateAnswers.fulfilled, (state, action) => {
				if (action.payload) {
					state.answerMakerStart.status = RequestStatus.IDLE;
					state.answerMakerStart.error = action.payload.error;
					state.answerMakerStart.message = action.payload.message;
				} else state.answerMakerStart.status = RequestStatus.FAILED;
			})
			.addCase(generateAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.answerMakerStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.answerMakerStart.error = error;
				state.answerMakerStart.message = message;
			})
			.addCase(stopGenerateAnswers.pending, (state) => {
				state.answerMakerStop.status = RequestStatus.LOADING;
			})
			.addCase(stopGenerateAnswers.fulfilled, (state, action) => {
				if (action.payload) {
					state.answerMakerStop.status = RequestStatus.IDLE;
					state.answerMakerStop.error = action.payload.error;
					state.answerMakerStop.message = action.payload.message;
				} else state.answerMakerStop.status = RequestStatus.FAILED;
			})
			.addCase(stopGenerateAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.answerMakerStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.answerMakerStop.error = error;
				state.answerMakerStop.message = message;
			})
			.addCase(getStatusGenerateAnswers.pending, (state) => {
				state.answerMakerStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusGenerateAnswers.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.answerMakerStatus.requestStatus = RequestStatus.IDLE;
					state.answerMakerStatus.error = action.payload.error;
					state.answerMakerStatus.responseStatus = action.payload.status;
					state.answerMakerStatus.progress = action.payload.progress;
				} else state.answerMakerStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusGenerateAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.answerMakerStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.answerMakerStatus.error = error.toString();
			});
	},
});

export const { clearState, clearAnswerMakerStart, clearAnswerMakerStop, clearAnswerMakerStatus } = qasAnswerMakerSlice.actions;

export const selectGeneratingAnswersStartStatus = (state: RootState) => state.qasAnswerMaker.answerMakerStart;
export const selectGeneratingAnswersStopStatus = (state: RootState) => state.qasAnswerMaker.answerMakerStop;
export const selectGeneratingAnswersProcessStatus = (state: RootState) => state.qasAnswerMaker.answerMakerStatus;

export default qasAnswerMakerSlice.reducer;
